import { gql } from "@apollo/client";
import {
  EmployeeItem,
  JumpToBarLibraryItem,
  JumpToItem,
  LocationItem,
  RoleGroupItem,
  RoleItem,
} from "@src/features/app/ui/jumpToBar/hooks/useAllJumpToItems";
import {
  useRecentlyViewedEmployeesQuery,
  useRecentlyViewedLibraryItemsQuery,
  useRecentlyViewedLocationGroupsQuery,
  useRecentlyViewedLocationsQuery,
  useRecentlyViewedRoleGroupsQuery,
  useRecentlyViewedRolesQuery,
} from "@src/features/app/ui/jumpToBar/hooks/useAllRecentlyViewedItems.generated";
import { Ff, LibraryItemType } from "@src/types.generated";
import { getMediaUrl } from "@src/features/contentDetail/utils/media";
import { orderBy } from "lodash";
import { DateTime } from "luxon";
import { useMemo } from "react";
import { useFeatureFlag } from "@src/features/app/hooks/useFeatureFlag";

type Return = {
  items: JumpToItem[];
};

const useAllRecentlyViewedItems = (): Return => {
  const scormResourcesEnabled = useFeatureFlag(Ff.ScormResources);
  const oneWeekAgo = useMemo(
    () => DateTime.now().minus({ weeks: 1 }).toISO(),
    [],
  );
  const variables = {
    openedStartDate: oneWeekAgo,
  };
  const { data: employeeData } = useRecentlyViewedEmployeesQuery({ variables });
  const { data: libraryItemsData } = useRecentlyViewedLibraryItemsQuery({
    fetchPolicy: "no-cache",
    variables: {
      input: {
        sort: { column: "openedAt", descending: true, nullsLast: true },
      },
      pagination: {
        limit: 10,
        offset: 0,
      },
    },
  });
  const { data: locationsData } = useRecentlyViewedLocationsQuery({
    variables,
  });
  const { data: rolesData } = useRecentlyViewedRolesQuery({ variables });
  const { data: locationGroupsData } = useRecentlyViewedLocationGroupsQuery({
    variables,
  });
  const { data: roleGroupsData } = useRecentlyViewedRoleGroupsQuery({
    variables,
  });
  const recentlyViewedItems = useMemo(() => {
    const employees: EmployeeItem[] =
      employeeData?.Employees.objects.map((employee) => {
        return {
          id: employee.id,
          name: employee.name || "",
          email: employee.email,
          phoneNumber: employee.phoneNumber,
          userType: employee.userType,
          openedAt: employee.openedAt,
          locations: employee.locations,
          type: "Employee",
          profileImage: employee.profileImage,
        };
      }) || [];
    const libraryItems: JumpToBarLibraryItem[] = [];
    libraryItemsData?.LibraryItems.objects.forEach((li) => {
      if (li.type === LibraryItemType.Module && li.module) {
        libraryItems.push({
          id: li.module.id,
          libraryItemId: li.id,
          type: "Module",
          searchHeadline: li.searchHeadline,
          coverImage: li.coverImage || undefined,
          name: li.name.en,
          openedAt: li.openedAt,
        });
      } else if (li.type === LibraryItemType.Course && li.course) {
        libraryItems.push({
          id: li.course.id,
          libraryItemId: li.id,
          type: "Course",
          searchHeadline: li.searchHeadline,
          coverImage: li.coverImage || undefined,
          name: li.name.en,
          openedAt: li.openedAt,
        });
      } else if (
        li.type === LibraryItemType.TrainingResource &&
        li.trainingResource
      ) {
        libraryItems.push({
          id: li.trainingResource.id,
          libraryItemId: li.id,
          type: "Resource",
          searchHeadline: li.searchHeadline,
          mediaUrl: li.trainingResource.publishedVersion?.media
            ? getMediaUrl(li.trainingResource.publishedVersion.media, {
                thumb: true,
              })
            : undefined,

          name: li.name?.en || "",
          openedAt: li.openedAt,
        });
      } else if (li.type === LibraryItemType.Skill && li.skill) {
        libraryItems.push({
          id: li.skill.id,
          libraryItemId: li.id,
          type: "Skill",
          coverImage: li.coverImage || undefined,
          name: li.name.en,
          searchHeadline: li.searchHeadline,
          openedAt: li.openedAt,
        });
      } else if (li.orgPremiumContentConfig) {
        const catalogModule =
          li.orgPremiumContentConfig.catalogConfig?.catalogModule;
        if (catalogModule) {
          const module = catalogModule.module;

          libraryItems.push({
            id: li.orgPremiumContentConfig.id,
            type: "PremiumModule",
            catalogModuleId: catalogModule.id,
            name: module.libraryItem.name.en,
            coverImage: module.libraryItem.coverImage || undefined,
            openedAt: li.openedAt,
            searchHeadline: module.libraryItem.searchHeadline,
          });
        }
      } else if (
        scormResourcesEnabled &&
        li.type === LibraryItemType.ScormCourse &&
        li.scormCourse
      ) {
        libraryItems.push({
          id: li.scormCourse.id,
          libraryItemId: li.id,
          type: "SCORMCourse",
          coverImage: li.coverImage || undefined,
          name: li.name.en,
          searchHeadline: li.searchHeadline,
          openedAt: li.openedAt,
        });
      }
    });
    const locations: LocationItem[] =
      locationsData?.Locations.objects.map((location) => {
        return {
          id: location.id,
          name: location.name,
          openedAt: location.openedAt,
          type: "Location",
        };
      }) || [];
    const roles: RoleItem[] =
      rolesData?.Roles.objects.map((role) => {
        return {
          id: role.id,
          name: role.name,
          openedAt: role.openedAt,
          type: "Role",
        };
      }) || [];
    const locationGroups: JumpToItem[] =
      locationGroupsData?.LocationGroups.objects.map((locationGroup) => {
        return {
          id: locationGroup.id,
          name: locationGroup.name,
          type: locationGroup.type,
          openedAt: locationGroup.openedAt,
        };
      }) || [];
    const roleGroups: RoleGroupItem[] =
      roleGroupsData?.RoleGroups.objects.map((roleGroup) => {
        return {
          id: roleGroup.id,
          name: roleGroup.name,
          openedAt: roleGroup.openedAt,
          type: "RoleGroup",
        };
      }) || [];
    return orderBy(
      [
        ...employees,
        ...libraryItems,
        ...roles,
        ...roleGroups,
        ...locations,
        ...locationGroups,
      ],
      "openedAt",
      "desc",
    ).filter((x) => !!x.openedAt);
  }, [
    employeeData?.Employees.objects,
    libraryItemsData?.LibraryItems.objects,
    locationsData?.Locations.objects,
    rolesData?.Roles.objects,
    locationGroupsData?.LocationGroups.objects,
    roleGroupsData?.RoleGroups.objects,
    scormResourcesEnabled,
  ]);

  return {
    items: recentlyViewedItems,
  };
};

export default useAllRecentlyViewedItems;

gql`
  query RecentlyViewedEmployees($openedStartDate: DateTime!) {
    Employees: AdminEmployees(openedStartDate: $openedStartDate) {
      objects {
        ...JumpToEmployee
        openedAt
      }
    }
  }
  query RecentlyViewedLibraryItems(
    $input: AdminLibraryInput!
    $pagination: PaginationInput
  ) {
    LibraryItems: AdminLibrary(input: $input, pagination: $pagination) {
      objects {
        id
        type
        module {
          id
        }
        course {
          id
        }
        trainingResource {
          id
        }
        skill {
          id
        }
        scormCourse {
          id
        }
        name {
          ...TranslationSet
        }
        searchHeadline
        coverImage {
          ...CoverImage
        }
        trainingResource {
          ...JumpToTrainingResource
        }
        orgPremiumContentConfig {
          ...JumpToOrgPremiumContentConfig
        }
        orgSharpConfig {
          id
        }
        openedAt
      }
    }
  }
  query RecentlyViewedCourses($openedStartDate: DateTime!) {
    CourseLibrary: AdminCourseLibrary(openedStartDate: $openedStartDate) {
      ...JumpToCourse
      libraryItem {
        id
        openedAt
      }
    }
  }
  query RecentlyViewedTrainingResources($openedStartDate: DateTime!) {
    TrainingResources: AdminTrainingResources(
      openedStartDate: $openedStartDate
    ) {
      ...JumpToTrainingResource
      libraryItem {
        id
        openedAt
      }
    }
  }
  query RecentlyViewedLocations($openedStartDate: DateTime!) {
    Locations(input: { openedStartDate: $openedStartDate }) {
      objects {
        ...JumpToLocation
        openedAt
      }
    }
  }
  query RecentlyViewedRoles($openedStartDate: DateTime!) {
    Roles(input: { openedStartDate: $openedStartDate }) {
      objects {
        ...JumpToRole
        openedAt
      }
    }
  }
  query RecentlyViewedLocationGroups($openedStartDate: DateTime!) {
    LocationGroups(input: { openedStartDate: $openedStartDate }) {
      objects {
        ...JumpToLocationGroup
        openedAt
      }
    }
  }
  query RecentlyViewedRoleGroups($openedStartDate: DateTime!) {
    RoleGroups(input: { openedStartDate: $openedStartDate }) {
      objects {
        ...JumpToRoleGroup
        openedAt
      }
    }
  }
`;
